import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertCircle } from 'lucide-react';
import { ContentListComponent } from '@/presentation/feature/content/list/ContentListComponent';
// import type { VirtualListHandle } from '@/presentation/components/virtualList/VirtualList';
import { AnimatedLoadingIndicator } from '@/presentation/components/common/loadingIndicators/LoadingIndicator';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { useFeed } from './hooks/useFeed';
import type { RootState } from '@/presentation/store';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * Feed Page - Main content feed using ContentListComponent
 * Displays videos in a list layout with infinite scroll
 * L
 */
export const FeedPage = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { medias, isLoading, hasMore, error, loadMore, refresh, initialize } = useFeed();
  const { autoTogglePlayPauseActiveVideo } = useVideoManagement();
  // use line below if manual scroll to item in the virtual list is needed
  // const virtualListRef = useRef<VirtualListHandle>(null);
  const [isPageActive, setIsPageActive] = useState(true);

  // Get navigation screen key from RouteStack context
  // This key is stable for this route instance and used for both navigation and query caching
  // IMPORTANT: Capture the screen key ONCE on mount and never change it
  // Initialize the ref with a value IMMEDIATELY to avoid undefined on first render
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  // Example: Scroll to item programmatically
  // virtualListRef.current?.scrollToIndex(5, 'smooth');

  // Base page lifecycle management (Flutter RouteObserver pattern)
  useBasePage({
    onInit: () => {
      // Initialize feed data on first mount
      // This gives us explicit control over when data loads
      initialize();

      return () => {
        // Cleanup on unmount (rare with KeepAlive)
      };
    },
    onPush: (repushed) => {
      // Called when first navigating to Feed page
      // Data already loaded in onInit
      // console.log('🧹 [FeedPage] onPush - ', screenKey, { repushed });

      if (repushed) {
        autoTogglePlayPauseActiveVideo();
      }

      setIsPageActive(true);
    },
    onPopNext: () => {
      // Called when returning to Feed page (user pressed back)
      // Resume video playback if there was an active video (respects manual pause)
      // console.log('🧹 [FeedPage] onPopNext - ', screenKey);
      autoTogglePlayPauseActiveVideo();

      setIsPageActive(true);
    },
    onPushNext: () => {
      // Called when leaving Feed page (new route pushed on top)
      // Pause any playing videos using video management hook
      autoTogglePlayPauseActiveVideo();

      setIsPageActive(false);
    },
    onPop: () => {
      // Called when Feed page is being popped (going back from Feed)
      autoTogglePlayPauseActiveVideo();

      setIsPageActive(false);
    },
    onDispose: () => {
      // Final cleanup (rare with KeepAlive caching)
    },
    cleanReactQueryBehaviour: 'none',
    debug: false, // Set to true to see lifecycle logs during development
  });

  const handleRefresh = () => {
    refresh();
  };

  // Coarse lifecycle token for the feed root, derived from existing state only.
  const feedStatus = error
    ? 'error'
    : isLoading && medias.length === 0
      ? 'loading'
      : medias.length === 0
        ? 'empty'
        : 'ready';

  return (
    <>
      <div
        {...qaAttr({ id: 'feed', status: feedStatus })}
        className="w-full py-8"
      >
        {/* Error State */}
        {error && (
          <div
            className="mb-6 p-4 border rounded-lg"
            style={{
              backgroundColor: theme.backgroundSecondary,
              borderColor: theme.border,
            }}
          >
            <div className="flex items-center space-x-2" style={{ color: theme.danger }}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{l10n.content.errorFetchingFeed}</span>
            </div>
            <button
              onClick={handleRefresh}
              className="mt-3 px-4 py-2 rounded"
              style={{ backgroundColor: theme.danger, color: theme.text.primary }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.dangerHover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.danger}
            >
              {l10n.common.tryAgain}
            </button>
          </div>
        )}

        {/* Video List */}
        <ContentListComponent
          screenKey={screenKey}
          qa={{ id: 'feed-list' }}
          // ref={virtualListRef}
          isActive={isPageActive}
          medias={medias}
          currentUserId={currentUser?.firebaseId}
          layout="list"
          columns={1}
          autoplay={true}
          showFullscreenButton={true}
          showMore={true}
          muted={false}
          isLoading={isLoading}
          hasMore={hasMore}
          gap={responsive.gap['6xl']}
          onReachedEnd={hasMore && !isLoading ? loadMore : undefined}
          // onVisibleItemsChange={handleVisibleItemsChange}
          emptyState={
            (
              <div className="text-center py-12">
                <div className="mb-4" style={{ color: theme.text.secondary }}>
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2" style={{ color: theme.text.primary }}>{l10n.content.noVideosAvailable}</h3>
              </div>
            )
          }
        />

        {/* Loading More Indicator */}
        {hasMore && isLoading && (
          <div className="py-8 flex justify-center">
            <AnimatedLoadingIndicator />
          </div>
        )}

        {/* End of Feed */}
        {!hasMore && medias.length > 0 && (
          <div className="py-8 text-center" style={{ color: theme.text.secondary }}>
            <div className="text-sm">{l10n.common.reachedEnd}</div>
          </div>
        )}
      </div>
    </>
  );
};

